import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@tg/core/components';
import { useCombinedRefs } from '@tg/core/hooks';

const PasswordField = ({
  required,
  name,
  errors,
  id,
  translationKey,
  disabled,
  defaultValue,
  register,
  autoComplete,
}) => {
  const { t } = useTranslation('forms');

  const label = t(`labels.${translationKey}`);
  const placeholder = t(`placeholders.${translationKey}`);
  const fieldId = `${id}.${name}`;

  // Show/Hide password functionality
  const [revealPw, setRevealPw] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const inputRef = useRef();
  const icon = revealPw ? 'eye slash' : 'eye';

  /**
   * Each time revealPw changes, re-focus the input and restore the cursor position
   */
  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(cursorPos, cursorPos);
  }, [revealPw]);

  return (
    <Form.Field required={required && !disabled} error={!!errors}>
      <label htmlFor={fieldId}>{label}</label>
      <div className='relative'>
        <input
          type={revealPw ? 'text' : 'password'}
          id={fieldId}
          name={fieldId}
          placeholder={placeholder}
          defaultValue={defaultValue}
          disabled={disabled}
          required={required}
          ref={useCombinedRefs(inputRef, register({ required }))}
          autoComplete={autoComplete}
        />

        <button
          type='button'
          onClick={() => {
            setCursorPos(inputRef.current.selectionStart);
            setRevealPw(!revealPw);
          }}
          className='absolute right-2.5 bottom-0 top-0 cursor-pointer focus:outline-none focus:ring'
        >
          <Icon name={icon} />
        </button>
      </div>
      {errors && (
        <p>{t(`validations.${errors.message.key}`, errors.message.values)}</p>
      )}
    </Form.Field>
  );
};

PasswordField.propTypes = {
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  errors: PropTypes.object,
  id: PropTypes.string.isRequired,
  translationKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  register: PropTypes.any.isRequired,
  autoComplete: PropTypes.oneOf(['new-password', 'current-password']),
};

PasswordField.defaultProps = {
  required: false,
  disabled: false,
  errors: null,
  defaultValue: '',
  autoComplete: null,
};

export default PasswordField;
